// rraPendingDecision — RRA v2.2 part B (WEL-180): the lease builder's "Decision pending" marks.
//
// While an ANIMAL accommodation request is open on a household, the lease no longer carries an
// indefinite $0. Staff tick the animal(s) the request is about in the builder's Animals section, and
// enter what the lease says applies if the request is not approved (rent per month and a deposit; they
// default to this draft's per-animal rates). A marked animal is $0 now; the lease prints one neutral
// pending-decision clause naming them. Unticked animals are charged normally.
//
// This module is the pure half: which animals can be marked, the key that identifies one (it must be the
// SAME key the server computes — name lower-cased, then '|', then dog / cat / other), the new list after a
// tick or an amount edit, the one-line summary, and the advisory staff read until something is marked.
// The tick writes `LeaseFormState.animalsPendingDecision`; the server heals stale entries on generate.
//
//   cd apps/web && npx tsx --test src/lib/rraPendingDecision.test.ts
//
// Staff copy only — nothing here reaches the lease (the lease clause is the API's and names no
// accommodation).

import {
  ANIMAL_DEPOSIT_PER_ANIMAL_DEFAULT_CENTS,
  ANIMAL_RENT_PER_ANIMAL_DEFAULT_CENTS,
  petToRow,
  type AppFolioPet,
  type LeaseFormState,
} from './leaseConfig';
import { formatMoneyCents } from './money';

export type PendingDecisionEntry = NonNullable<LeaseFormState['animalsPendingDecision']>[number];

/** The tick's label. */
export const DECISION_PENDING_LABEL = 'Decision pending';
/** Shown while an animal request is open and no animal is marked yet (never blocks). */
export const PENDING_ADVISORY = 'Accommodation request open — mark the animal(s) it concerns in Animals';
export const PENDING_HELP =
  'An accommodation request about an animal is open. Tick the animal(s) it concerns: they are $0 now, and the lease says the charges below apply from the first of the month after our written notice if the request is not approved. Animals you leave unticked are charged normally.';

/**
 * The same coercion the API makes (apps/api/src/lib/leaseIntakeForm.ts coerceAnimalType): a pet's
 * displayed kind becomes dog, cat or other — "rabbit" is other.
 */
export function coerceAnimalType(rawType: string): 'dog' | 'cat' | 'other' {
  const t = rawType.toLowerCase();
  if (/\bdog\b|puppy|canine/.test(t)) return 'dog';
  if (/\bcat\b|kitten|feline/.test(t)) return 'cat';
  return 'other';
}

/**
 * The identity of one animal in this draft, built exactly as the server builds it
 * (`markableAnimalsOf`): its name lower-cased, '|', then dog / cat / other from the pet's DISPLAYED type —
 * or nothing when the pet has no type (`rex|`; an empty type part matches any kind). '' when it has no
 * name. A tick made from the draft's own pets before the summary loads therefore carries the SAME key the
 * summary's roster later hands back, and stays ticked instead of being duplicated.
 */
export function animalDecisionKey(pet: AppFolioPet): string {
  const row = petToRow(pet);
  const name = row.name.trim().toLowerCase();
  if (!name) return '';
  const type = row.type.trim() ? coerceAnimalType(row.type) : '';
  return `${name}|${type}`;
}

/** An animal can be marked only when it has a name (the key needs one). */
export function canMarkPending(pet: AppFolioPet): boolean {
  return animalDecisionKey(pet) !== '';
}

/** One animal the builder can offer a `Decision pending` tick for. */
export interface MarkableAnimal {
  /** The identity written to `animalsPendingDecision[].key` — exactly as the source gave it. */
  key: string;
  name: string;
  type?: 'dog' | 'cat' | 'other';
  /** What the row reads: `Rex · dog · Lab mix`. */
  label: string;
  /** Why it cannot be ticked, when it cannot: already at no charge, or no name to key it by. */
  blocked?: 'exempt' | 'unnamed';
}

/** The review summary's `rraRequestOpen.markableAnimals` entry: the merged roster generate itself uses. */
export interface ServerMarkableAnimal {
  key: string;
  name: string;
  type?: 'dog' | 'cat' | 'other';
}

/** An animal already at $0 through an exemption (an approval on file, or a build-time note) cannot also be pending. */
function petIsExempt(pet: AppFolioPet): boolean {
  return (
    (typeof pet.chargeExemptionCaseItemId === 'string' && pet.chargeExemptionCaseItemId.length > 0) ||
    pet.raWaiveBuildTime === true
  );
}

/** The draft's own pet list as markable rows — the fallback while the summary does not send `markableAnimals`. */
export function markableFromPets(pets: ReadonlyArray<AppFolioPet>): MarkableAnimal[] {
  return pets.map((pet) => {
    const row = petToRow(pet);
    const key = animalDecisionKey(pet);
    const blocked = key === '' ? 'unnamed' : petIsExempt(pet) ? 'exempt' : undefined;
    return {
      key,
      name: row.name.trim(),
      ...(key && row.type.trim() ? { type: coerceAnimalType(row.type) } : {}),
      label: [row.name || 'Unnamed animal', row.type, row.breed].filter(Boolean).join(' · '),
      ...(blocked ? { blocked } : {}),
    };
  });
}

/** The server's merged roster (AppFolio pets + tenant-intake animals, exempt ones already excluded) as markable rows. */
export function markableFromServer(list: ReadonlyArray<ServerMarkableAnimal>): MarkableAnimal[] {
  return list.map((a) => {
    const name = (a.name ?? '').trim();
    const usable = Boolean(a.key) && name !== '';
    return {
      key: a.key,
      name,
      ...(a.type ? { type: a.type } : {}),
      label: [name || 'Unnamed animal', a.type].filter(Boolean).join(' · '),
      ...(usable ? {} : { blocked: 'unnamed' as const }),
    };
  });
}

/**
 * What the builder offers to mark: the server's roster when the summary sends one (so an animal the
 * applicant added only on the intake can be marked, and each key is written exactly as given), else the
 * draft's own pets.
 */
export function markableAnimalsFor(
  server: ReadonlyArray<ServerMarkableAnimal> | undefined,
  pets: ReadonlyArray<AppFolioPet>,
): MarkableAnimal[] {
  return server !== undefined ? markableFromServer(server) : markableFromPets(pets);
}

/** The if-not-approved amounts a fresh tick starts with: this draft's per-animal rates (the house defaults when blank). */
export function defaultIfDenied(form: Pick<LeaseFormState, 'animalRentPerAnimal' | 'animalDepositPerAnimal'>): {
  rentCents: number;
  depositCents: number;
} {
  const rent = Number.isFinite(form.animalRentPerAnimal) ? form.animalRentPerAnimal : 0;
  const deposit = Number.isFinite(form.animalDepositPerAnimal) ? form.animalDepositPerAnimal : 0;
  return {
    rentCents: rent > 0 ? rent : ANIMAL_RENT_PER_ANIMAL_DEFAULT_CENTS,
    depositCents: deposit > 0 ? deposit : ANIMAL_DEPOSIT_PER_ANIMAL_DEFAULT_CENTS,
  };
}

export function pendingEntryFor(
  animal: Pick<MarkableAnimal, 'key'>,
  list: ReadonlyArray<PendingDecisionEntry> | undefined,
): PendingDecisionEntry | undefined {
  return animal.key ? (list ?? []).find((e) => e.key === animal.key) : undefined;
}

/** Tick or untick one animal. An empty result is `undefined`, so a draft with nothing marked carries no field at all. */
export function togglePending(
  list: ReadonlyArray<PendingDecisionEntry> | undefined,
  animal: MarkableAnimal,
  on: boolean,
  form: Pick<LeaseFormState, 'animalRentPerAnimal' | 'animalDepositPerAnimal'>,
): PendingDecisionEntry[] | undefined {
  const key = animal.key;
  if (!key) return list ? [...list] : undefined;
  const without = (list ?? []).filter((e) => e.key !== key);
  if (!on) return without.length > 0 ? without : undefined;
  if (animal.blocked) return list ? [...list] : undefined; // an exempt or unnamed animal is never marked
  if (without.length !== (list ?? []).length) return [...(list ?? [])]; // already ticked
  const d = defaultIfDenied(form);
  const entry: PendingDecisionEntry = {
    key, // exactly as the source gave it
    name: animal.name,
    ...(animal.type ? { type: animal.type } : {}),
    ifDeniedRentCents: d.rentCents,
    ifDeniedDepositCents: d.depositCents,
  };
  return [...(list ?? []), entry];
}

/** Edit one marked animal's if-not-approved amounts (negative or non-finite input is clamped to 0). */
export function setPendingAmount(
  list: ReadonlyArray<PendingDecisionEntry> | undefined,
  key: string,
  field: 'ifDeniedRentCents' | 'ifDeniedDepositCents',
  cents: number,
): PendingDecisionEntry[] | undefined {
  if (!list) return undefined;
  const safe = Number.isFinite(cents) && cents > 0 ? Math.round(cents) : 0;
  return list.map((e) => (e.key === key ? { ...e, [field]: safe } : e));
}

/** The totals the lease clause will print: the SUM of the marked animals' amounts. */
export function pendingTotals(list: ReadonlyArray<PendingDecisionEntry> | undefined): {
  count: number;
  rentCents: number;
  depositCents: number;
} {
  const entries = list ?? [];
  return {
    count: entries.length,
    rentCents: entries.reduce((n, e) => n + (e.ifDeniedRentCents || 0), 0),
    depositCents: entries.reduce((n, e) => n + (e.ifDeniedDepositCents || 0), 0),
  };
}

/** `2 animals marked — $0 now; if not approved: $100.00 per month, $1,000.00 deposit` — or null with none marked. */
export function pendingSummaryLine(list: ReadonlyArray<PendingDecisionEntry> | undefined): string | null {
  const t = pendingTotals(list);
  if (t.count === 0) return null;
  return `${t.count} ${t.count === 1 ? 'animal' : 'animals'} marked — $0 now; if not approved: ${formatMoneyCents(t.rentCents)} per month, ${formatMoneyCents(t.depositCents)} deposit`;
}

/**
 * The advisory above the marks: while an animal request is open and nothing is marked (it never blocks
 * a send). Null once something is marked, or when no animal request is open.
 */
export function pendingAdvisory(animalRequestOpen: boolean, list: ReadonlyArray<PendingDecisionEntry> | undefined): string | null {
  return animalRequestOpen && (list ?? []).length === 0 ? PENDING_ADVISORY : null;
}
